// Built-in new-order chime (Web Audio, no asset files) + global repeating alert.
let ctx = null;
let loopTimer = null;
let ticks = 0;
let pending = false;
const MUTE_KEY = 'd7-alert-muted';
const listeners = new Set();

const context = () => {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  return ctx;
};

export const isMuted = () => localStorage.getItem(MUTE_KEY) === '1';
export const audioUnlocked = () => !!ctx && ctx.state === 'running';
export const alertRinging = () => !!loopTimer;
export const onAlertChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = () => listeners.forEach((fn) => fn({ ringing: alertRinging(), unlocked: audioUnlocked(), muted: isMuted() }));

export const unlockAudio = async () => {
  const c = context();
  if (!c) return false;
  try { if (c.state === 'suspended') await c.resume(); } catch { /* noop */ }
  const ok = c.state === 'running';
  if (ok && pending) { pending = false; startAlert(); }
  emit();
  return ok;
};

// Any user gesture unlocks the audio context (browsers block autoplay until then).
export const keepUnlocked = () => {
  const handler = () => { if (!audioUnlocked()) unlockAudio(); };
  ['pointerdown', 'keydown', 'touchstart'].forEach((e) => document.addEventListener(e, handler, { passive: true }));
  return () => ['pointerdown', 'keydown', 'touchstart'].forEach((e) => document.removeEventListener(e, handler));
};

const note = (c, freq, start, dur, gain = 0.3) => {
  const osc = c.createOscillator();
  const vol = c.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  vol.gain.setValueAtTime(0, start);
  vol.gain.linearRampToValueAtTime(gain, start + 0.02);
  vol.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(vol).connect(c.destination);
  osc.start(start);
  osc.stop(start + dur + 0.05);
};

// Three bright double-notes - clearly audible in a noisy kitchen.
export const playChime = (repeats = 3) => {
  const c = context();
  if (!c || c.state !== 'running') return false;
  const t0 = c.currentTime + 0.02;
  for (let i = 0; i < repeats; i += 1) {
    const s = t0 + i * 0.42;
    note(c, 1318.5, s, 0.22);
    note(c, 1760, s + 0.14, 0.3);
  }
  return true;
};

export const stopAlert = () => {
  if (loopTimer) clearTimeout(loopTimer);
  loopTimer = null;
  ticks = 0;
  pending = false;
  emit();
};

// Rings every 4s (up to `maxRepeats`) until the user acknowledges it.
export const startAlert = (maxRepeats = 10) => {
  if (isMuted()) return false;
  if (!audioUnlocked()) { pending = true; unlockAudio(); emit(); return false; }
  if (loopTimer) clearTimeout(loopTimer);
  ticks = 0;
  const tick = () => {
    playChime();
    ticks += 1;
    loopTimer = ticks < maxRepeats ? setTimeout(tick, 4000) : null;
    if (!loopTimer) emit();
  };
  tick();
  emit();
  return true;
};

export const setMuted = (value) => {
  localStorage.setItem(MUTE_KEY, value ? '1' : '0');
  if (value) stopAlert(); else emit();
};

export const alertPending = () => pending;
